import { Calendar, Check, Clock, Edit2, Printer, Target, Trash2, X, Zap } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { CalendarEntry } from '../../types/database.tables'
import type { WorkoutWithData } from '../../types/workout'
import { formatLongDate, formatTime } from '../../utils/dateUtils'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { ActionButton } from '../common/ActionButton'
import { Modal } from '../common/Modal'

// Interface for lap data
interface LapData {
  id: number
  duration: number // in seconds
  avgPower: number // in watts
  targetPower?: number // target watts for compliance calculation
  avgVelocity: number // in km/h
  avgHeartRate: number // in bpm
  energyUse: number // in kJ
  energyComposition: {
    carbs: number // percentage
    fat: number // percentage
  }
  compliance: 'good' | 'warning' | 'poor'
}

// Helper function to convert URLs to clickable links
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index.toString()}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: theme.colors.primary,
            textDecoration: 'underline',
            wordBreak: 'break-all',
          }}
        >
          {part}
        </a>
      )
    }
    return part
  })
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`

const TitleSummary = styled.span`
  margin-left: ${theme.spacing.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const ModalSubtitle = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const ModalBody = styled.div`
  padding: ${theme.spacing.lg};
`

const DetailSection = styled.div`
  margin-bottom: ${theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sm};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0 0 ${theme.spacing.sm} 0;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};

  &:last-child {
    margin-bottom: 0;
  }
`

const DetailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
`

const DetailLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  min-width: 80px;
`

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  flex: 1;
`

const InstructionsText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
`

const CompletionButton = styled.button<{ $isCompleted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 2px solid ${(props) => (props.$isCompleted ? props.theme.colors.border : props.theme.colors.primary)};
  border-radius: ${theme.borderRadius.md};
  background: ${(props) => (props.$isCompleted ? props.theme.colors.surface : props.theme.colors.primaryLight)};
  color: ${(props) => (props.$isCompleted ? props.theme.colors.textMuted : props.theme.colors.text)};
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transitions.default};
  margin-top: ${theme.spacing.lg};

  &:hover {
    background: ${(props) => (props.$isCompleted ? props.theme.colors.surfaceHover : props.theme.colors.surfaceHover)};
    border-color: ${(props) => (props.$isCompleted ? props.theme.colors.border : props.theme.colors.primary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ClickableTime = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.sm};
  cursor: pointer;
  padding: 0;
  margin-left: ${theme.spacing.sm};
  vertical-align: middle;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
    text-decoration-style: solid;
  }
`

const Label = styled.span`
  font-weight: 600;
  padding-bottom: ${theme.spacing.lg};
  padding-left: ${theme.spacing.xs};
`

const HeaderScheduleForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-left: ${theme.spacing.sm};
`

const HeaderTimeSelect = styled.select`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:first-of-type {
    width: 60px; /* Hours: 00-23 */
  }

  &:last-of-type {
    width: 60px; /* Minutes: 00, 15, 30, 45 */
  }
`

const TimeSelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const TimeSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  font-weight: 500;
`

// Laps section components
const LapsContainer = styled.div`
  margin-top: ${theme.spacing.lg};
`

const LapsTitle = styled.h3`
  font-size: ${theme.typography.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const LapsChart = styled.div`
  display: flex;
  align-items: end;
  gap: 2px;
  height: 200px;
  padding: ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  position: relative;
`

const LapBar = styled.div<{
  $height: number
  $width: number
  $compliance: 'good' | 'warning' | 'poor'
}>`
  height: ${({ $height }) => $height}px;
  min-width: ${({ $width }) => Math.max($width, 8)}px;
  max-width: ${({ $width }) => $width}px;
  background: ${({ $compliance, theme }) =>
    $compliance === 'good' ? '#10B981' : $compliance === 'warning' ? '#F59E0B' : '#EF4444'};
  border-radius: 2px 2px 0 0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-2px);
  }
`

const LapTooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${({ theme }) => theme.colors.background};
  }
`

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xs};
  
  &:last-child {
    margin-bottom: 0;
  }
`

const TooltipLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`

const TooltipValue = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const LapsLegend = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.md};
  font-size: ${theme.typography.sm};
`

const LegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &::before {
    content: '';
    width: 12px;
    height: 12px;
    background: ${({ $color }) => $color};
    border-radius: 2px;
  }
`

const HeaderActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xs};
  border: 0;
  border-radius: ${theme.borderRadius.sm};
  background: ${(props) => (props.$variant === 'secondary' ? props.theme.colors.surface : props.theme.colors.primaryLight)};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Mock lap data generator for cycling workouts
const generateMockLapData = (): LapData[] => {
  const laps: LapData[] = []
  const totalLaps = 12 + Math.floor(Math.random() * 8) // 12-20 laps

  for (let i = 0; i < totalLaps; i++) {
    const basePower = 180 + Math.random() * 120 // 180-300W base
    const targetPower = 200 + (i % 4) * 50 // Varying target power for intervals
    const powerDiff = Math.abs(basePower - targetPower) / targetPower

    // Determine compliance based on power difference
    let compliance: 'good' | 'warning' | 'poor' = 'good'
    if (powerDiff > 0.15) compliance = 'poor'
    else if (powerDiff > 0.08) compliance = 'warning'

    // Generate realistic related metrics
    const duration = 120 + Math.random() * 480 // 2-10 minutes
    const velocity = 25 + Math.random() * 15 // 25-40 km/h
    const heartRate = Math.floor(135 + (basePower / 300) * 50 + Math.random() * 15) // 135-185 bpm
    const energyUse = (basePower * duration) / 1000 // kJ calculation

    // Energy composition varies with intensity
    const intensity = basePower / 300
    const carbsPercentage = Math.floor(40 + intensity * 40 + Math.random() * 10)

    laps.push({
      id: i + 1,
      duration,
      avgPower: Math.floor(basePower),
      targetPower,
      avgVelocity: Math.floor(velocity * 10) / 10,
      avgHeartRate: heartRate,
      energyUse: Math.floor(energyUse * 10) / 10,
      energyComposition: {
        carbs: carbsPercentage,
        fat: 100 - carbsPercentage,
      },
      compliance,
    })
  }

  return laps
}

interface WorkoutDetailModalProps {
  isOpen: boolean
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
  onComplete?: (workoutId: string, completed: boolean) => void
  onSchedule?: (workoutId: string, time: string) => void
  workout?: WorkoutWithData
  entry?: CalendarEntry
}

export const WorkoutDetailModal: React.FC<WorkoutDetailModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onComplete,
  onSchedule,
  workout,
  entry,
}) => {
  const [isScheduling, setIsScheduling] = useState(false)
  const [selectedHour, setSelectedHour] = useState('')
  const [selectedMinute, setSelectedMinute] = useState('')
  const [hoveredLapId, setHoveredLapId] = useState<number | null>(null)

  const item = workout || entry

  const entryType = workout?.activity_type || entry?.entry_type || 'other'
  const EntryTypeIconComponent = getEntryIcon(entryType)
  const isCompleted = workout?.status === 'completed'
  const hasPlannedTime = workout?.time && workout.time.trim() !== ''
  let entryTitle = workout?.title || entry?.title
  if (entry?.entry_type === 'rest_day' && !entryTitle) {
    entryTitle = 'Rest Day'
  }

  const handleCompletionToggle = () => {
    if (workout && onComplete) {
      onComplete(workout.id, !isCompleted)
      onClose()
    }
  }

  const handleScheduleClick = () => {
    setIsScheduling(true)

    // Set initial values based on current workout time or default to 12:00
    if (workout?.time) {
      const [hours, minutes] = workout.time.split(':')
      setSelectedHour(hours.padStart(2, '0'))
      // Round minutes to nearest 15-minute interval
      const minuteInt = Number.parseInt(minutes, 10)
      const roundedMinutes = Math.round(minuteInt / 15) * 15
      setSelectedMinute(roundedMinutes.toString().padStart(2, '0'))
    } else {
      // Default to 12:00
      setSelectedHour('12')
      setSelectedMinute('00')
    }
  }

  const handleScheduleCancel = () => {
    setIsScheduling(false)
    setSelectedHour('')
    setSelectedMinute('')
  }

  const handleScheduleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (workout && onSchedule && selectedHour && selectedMinute) {
      const timeString = `${selectedHour}:${selectedMinute}:00`
      onSchedule(workout.id, timeString)
      setIsScheduling(false)
      setSelectedHour('')
      setSelectedMinute('')
      workout.time = timeString
    }
  }

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
      return () => {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }
  }, [isOpen, onClose])

  const lapData = useMemo(() => {
    if (!workout) return []
    const isWorkout = !!workout
    const shouldShowLaps = isWorkout && workout.status === 'completed' && workout?.activity_type === 'cycling'
    return shouldShowLaps ? generateMockLapData() : []
  }, [workout])

  if (!item) return null

  const isWorkout = !!workout
  const isEntry = !!entry

  // Generate lap data for completed cycling workouts
  const shouldShowLaps = isWorkout && isCompleted && workout?.activity_type === 'cycling'

  // Calculate dimensions for bars
  const maxPower = lapData.length > 0 ? Math.max(...lapData.map((lap) => lap.avgPower)) : 0
  const maxDuration = lapData.length > 0 ? Math.max(...lapData.map((lap) => lap.duration)) : 0

  const renderLapsSection = () => {
    if (!shouldShowLaps) return null

    return (
      <LapsContainer>
        <LapsTitle>
          <Zap size={18} />
          Laps ({lapData.length})
        </LapsTitle>
        <LapsChart>
          {lapData.map((lap) => {
            const barHeight = (lap.avgPower / maxPower) * 160 // Max height 160px
            const barWidth = (lap.duration / maxDuration) * 80 + 20 // Min 20px, max 100px
            const isHovered = hoveredLapId === lap.id

            return (
              <LapBar
                key={lap.id}
                $height={barHeight}
                $width={barWidth}
                $compliance={lap.compliance}
                onMouseEnter={() => setHoveredLapId(lap.id)}
                onMouseLeave={() => setHoveredLapId(null)}
              >
                <LapTooltip $visible={isHovered}>
                  <TooltipRow>
                    <TooltipLabel>Lap:</TooltipLabel>
                    <TooltipValue>{lap.id}</TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Duration:</TooltipLabel>
                    <TooltipValue>
                      {Math.floor(lap.duration / 60)}:{(lap.duration % 60).toString().padStart(2, '0')}
                    </TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Avg Power:</TooltipLabel>
                    <TooltipValue>{lap.avgPower}W</TooltipValue>
                  </TooltipRow>
                  {lap.targetPower && (
                    <TooltipRow>
                      <TooltipLabel>Target:</TooltipLabel>
                      <TooltipValue>{lap.targetPower}W</TooltipValue>
                    </TooltipRow>
                  )}
                  {lap.targetPower && (
                    <TooltipRow>
                      <TooltipLabel>Diff:</TooltipLabel>
                      <TooltipValue>
                        {lap.avgPower > lap.targetPower ? '+' : ''}
                        {lap.avgPower - lap.targetPower}W
                      </TooltipValue>
                    </TooltipRow>
                  )}
                  <TooltipRow>
                    <TooltipLabel>Velocity:</TooltipLabel>
                    <TooltipValue>{lap.avgVelocity} km/h</TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Heart Rate:</TooltipLabel>
                    <TooltipValue>{lap.avgHeartRate} bpm</TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Energy:</TooltipLabel>
                    <TooltipValue>{lap.energyUse} kJ</TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Carbs:</TooltipLabel>
                    <TooltipValue>{lap.energyComposition.carbs}%</TooltipValue>
                  </TooltipRow>
                  <TooltipRow>
                    <TooltipLabel>Fat:</TooltipLabel>
                    <TooltipValue>{lap.energyComposition.fat}%</TooltipValue>
                  </TooltipRow>
                </LapTooltip>
              </LapBar>
            )
          })}
        </LapsChart>
        <LapsLegend>
          <LegendItem $color="#10B981">On Target</LegendItem>
          <LegendItem $color="#F59E0B">Close to Target</LegendItem>
          <LegendItem $color="#EF4444">Off Target</LegendItem>
        </LapsLegend>
      </LapsContainer>
    )
  }

  const headerContent = (
    <ModalHeader>
      <HeaderLeft>
        <ModalTitle>
          <EntryTypeIconComponent size={16} />
          <TitleSummary>{entryTitle}</TitleSummary>
        </ModalTitle>
        <ModalSubtitle>
          <Calendar size={16} />
          {formatLongDate(isWorkout ? workout.scheduled_date : entry?.date)}
          {(isWorkout && !isScheduling ? workout.time : entry?.start_time) && (
            <ClickableTime onClick={handleScheduleClick} title="Click to change time">
              <Clock size={16} />
              <Label>{formatTime(isWorkout ? workout.time : entry?.start_time)}</Label>
            </ClickableTime>
          )}
          {isWorkout && !hasPlannedTime && !isScheduling && (
            <ClickableTime onClick={handleScheduleClick} title="Click to schedule time">
              <Clock size={16} style={{ verticalAlign: 'top' }} />
              <Label>Schedule time</Label>
            </ClickableTime>
          )}
          {isWorkout && isScheduling && (
            <HeaderScheduleForm onSubmit={handleScheduleSubmit}>
              <TimeSelectContainer>
                <HeaderTimeSelect
                  value={selectedHour}
                  onChange={(e) => setSelectedHour(e.target.value)}
                  required
                  autoFocus
                >
                  <option value="" />
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i.toString()} value={i.toString().padStart(2, '0')}>
                      {i.toString().padStart(2, '0')}
                    </option>
                  ))}
                </HeaderTimeSelect>
                <TimeSeparator>:</TimeSeparator>
                <HeaderTimeSelect value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} required>
                  <option value="" />
                  <option value="00">00</option>
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                </HeaderTimeSelect>
              </TimeSelectContainer>
              <HeaderActionButton type="submit" disabled={!selectedHour || !selectedMinute}>
                <Check size={14} />
              </HeaderActionButton>
              <HeaderActionButton type="button" $variant="secondary" onClick={handleScheduleCancel}>
                <X size={14} />
              </HeaderActionButton>
            </HeaderScheduleForm>
          )}
        </ModalSubtitle>
      </HeaderLeft>
      <HeaderActions>
        <ActionButton variant="edit" title="Print" disabled={true}>
          <Printer size={18} />
        </ActionButton>
        <ActionButton variant="edit" onClick={onEdit} title="Edit">
          <Edit2 size={18} />
        </ActionButton>
        <ActionButton variant="delete" onClick={onDelete} title="Delete">
          <Trash2 size={18} />
        </ActionButton>
        <ActionButton onClick={onClose} title="Close">
          <X size={18} />
        </ActionButton>
      </HeaderActions>
    </ModalHeader>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="900px" header={headerContent}>
      <ModalBody>
        {isWorkout && (
          <>
            <DetailSection>
              <DetailRow>
                <DetailIcon>
                  <Clock size={16} />
                </DetailIcon>
                <DetailValue>
                  Planned: {workout.duration_minutes} minutes
                  {workout.executedTotalDurationSeconds && (
                    <>, Completed: {Math.floor(workout.executedTotalDurationSeconds / 60)} minutes</>
                  )}
                </DetailValue>
              </DetailRow>

              {workout.goal && (
                <DetailRow>
                  <DetailIcon>
                    <Target size={16} />
                  </DetailIcon>
                  <DetailValue>{workout.goal}</DetailValue>
                </DetailRow>
              )}
            </DetailSection>

            {workout.summary && (
              <DetailSection>
                <DetailRow>
                  <DetailIcon />
                  <DetailValue>
                    <SectionTitle>Summary</SectionTitle>
                    <InstructionsText>{linkifyText(workout.summary)}</InstructionsText>
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {workout.status !== 'completed' && workout.instructions && (
              <DetailSection>
                <DetailRow>
                  <DetailIcon />
                  <DetailValue>
                    <SectionTitle>Instructions</SectionTitle>
                    <InstructionsText>{linkifyText(workout.instructions)}</InstructionsText>
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {/* Laps section for completed cycling workouts */}
            {renderLapsSection()}
          </>
        )}

        {isEntry && (
          <>
            {entry.start_time && entry.end_time && (
              <DetailSection>
                <SectionTitle>Details</SectionTitle>
                <DetailRow>
                  <DetailIcon>
                    <Clock size={16} />
                  </DetailIcon>
                  <DetailLabel>Time:</DetailLabel>
                  <DetailValue>
                    {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {entry.description && (
              <DetailSection>
                <SectionTitle>Description</SectionTitle>
                <InstructionsText>{linkifyText(entry.description)}</InstructionsText>
              </DetailSection>
            )}
          </>
        )}

        {isWorkout && onComplete && (
          <CompletionButton $isCompleted={isCompleted} onClick={handleCompletionToggle}>
            <Check size={20} />
            {isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
          </CompletionButton>
        )}
      </ModalBody>
    </Modal>
  )
}
